import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormField,
  FormItem,
  NewButton,
  Textarea,
  VStack,
  legacyTextStyles,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const appointmentDetailsChiefComplaintReasonForVisitFragment = graphql(`
  fragment AppointmentDetailsChiefComplaintReasonForVisit on Appointments
  @_unmask {
    __typename
    id
    chiefComplaint
    patientReasonForVisit
  }
`);

export const AppointmentDetailsChiefComplaintReasonForVisit = ({
  appointmentId,
  tenantId,
}: {
  appointmentId: string;
  tenantId: string;
}) => {
  const data = useCompleteFragment({
    fragment: appointmentDetailsChiefComplaintReasonForVisitFragment,
    key: {
      id: appointmentId,
    },
  });
  const toast = useToast();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateAppointmentDetailsMutation = graphql(
    `
      mutation UpdateAppointmentChiefComplaint(
        $tenantId: uuid!
        $id: uuid!
        $input: AppointmentsSetInput!
      ) {
        updateAppointmentsByPk(
          pkColumns: { tenantId: $tenantId, id: $id }
          _set: $input
        ) {
          ...AppointmentDetailsChiefComplaintReasonForVisit
        }
      }
    `,
    [appointmentDetailsChiefComplaintReasonForVisitFragment],
  );

  const [updateAppointmentDetails] = useMutation(
    updateAppointmentDetailsMutation,
    {
      optimisticResponse: ({ input, id }) => {
        return {
          updateAppointmentsByPk: {
            __typename: 'Appointments' as const,
            id,
            chiefComplaint: input.chiefComplaint ?? data.chiefComplaint,
            patientReasonForVisit:
              input.patientReasonForVisit ?? data.patientReasonForVisit,
          },
        };
      },
      onError: () => {
        toast.error('The appointment details could not be updated.');
      },
    },
  );

  const form = useForm({
    values: {
      chiefComplaint: data.chiefComplaint ?? '',
      patientReasonForVisit: data.patientReasonForVisit ?? '',
    },
  });

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const values = form.getValues();
      await updateAppointmentDetails({
        variables: {
          tenantId,
          id: appointmentId,
          input: {
            chiefComplaint: values.chiefComplaint || null,
            patientReasonForVisit: values.patientReasonForVisit || null,
          },
        },
      });
      setIsDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <NewButton
          wFull
          type="outline"
          icon={{ name: 'Plus' }}
          text={
            data.chiefComplaint || data.patientReasonForVisit
              ? 'Update Context'
              : 'Add Context'
          }
        />
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Chief Complaint & Reason For Visit</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <VStack>
            <span
              className={legacyTextStyles.body({
                size: 'm',
                weight: 'regular',
                color: 'secondary',
              })}
            >
              Chief Complaint
            </span>
            <FormField
              control={form.control}
              name="chiefComplaint"
              render={({ field }) => (
                <FormItem className="w-full">
                  <Textarea
                    {...field}
                    placeholder="Enter chief complaint"
                    className="min-h-[80px] w-full resize-none"
                  />
                </FormItem>
              )}
            />

            <span
              className={legacyTextStyles.body({
                size: 'm',
                weight: 'regular',
                color: 'secondary',
              })}
            >
              Reason For Visit
            </span>
            <FormField
              control={form.control}
              name="patientReasonForVisit"
              render={({ field }) => (
                <FormItem className="w-full">
                  <Textarea
                    {...field}
                    placeholder="Enter reason for visit"
                    className="min-h-[80px] w-full resize-none"
                  />
                </FormItem>
              )}
            />
          </VStack>
        </Form>

        <DialogFooter>
          <NewButton
            type="outline"
            text="Cancel"
            onClick={() => setIsDialogOpen(false)}
          />
          <NewButton
            type="primary"
            text="Save"
            onClick={handleSave}
            loading={isSubmitting}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
